/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, createContext, type ForwardedRef, forwardRef, isValidElement, type ReactElement } from 'react';
import { type ContextValue, DEFAULT_SLOT, GridListItem as GridListItemCore, type GridListItemProps as GridListItemCoreProps, Provider, useContextProps } from 'react-aria-components';
import { MoreVerticalStrokeIcon } from '../../icons/more-vertical-stroke/more-vertical-stroke.icon';
import { $ } from '../../tokens.stylex';
import { AvatarContext } from '../avatar/avatar.component';
import { BadgeContext } from '../badge/badge.component';
import { CheckboxContext } from '../checkbox/checkbox.component';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { IconContext } from '../icon/icon.component';
import { KbdContext } from '../kbd/kbd.component';
import { NumberBadgeContext } from '../number-badge/number-badge.component';
import { SwitchContext } from '../switch/switch.component';
import { TextButtonContext } from '../text-button/text-button.component';
import { Text, TextContext } from '../text/text.component';

interface GridListItemProps<T> extends Omit<GridListItemCoreProps<T>, 'style' | 'className'> {
  /** The size of the grid list item. (Default: 40) */
  size?: keyof typeof sizeVariants;
  /** Whether the grid list item is critical. (Default: false) */
  isCritical?: boolean;
  style?: stylex.StyleXStyles;
}

const GridListItemContext = createContext<ContextValue<GridListItemProps<object>, HTMLDivElement>>({});

const GridListItem = forwardRef((props: GridListItemProps<object>, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, GridListItemContext);

  const { style, size = 40, isCritical = false, ...coreProps } = props;

  return (
    <GridListItemCore
      ref={ref}
      {...stylex.props(styles.base, sizeVariants[size], props.isCritical && criticalStyles.base, style)}
      {...coreProps}>
      {(renderProps) => {
        const resolvedChildren = typeof coreProps.children === 'function' ? coreProps.children(renderProps) : coreProps.children;

        const isTextOnly = Children.toArray(resolvedChildren).every(c => !isValidElement(c));

        return (
          <Provider values={[
            [TextContext, {
              slots: {
                [DEFAULT_SLOT]: {
                  size: size === 52 ? 16 : 14,
                  style: [styles.firstRow, styles.fullWidth, textStyles.base],
                  color: (
                    (renderProps.isDisabled && 'disabled') ||
                    (isCritical && 'critical') ||
                    ((renderProps.isHovered || renderProps.isFocused || renderProps.isSelected) && 'primary') ||
                    'secondary'
                  ),
                  weight: renderProps.isSelected ? 'medium' : 'regular',
                },
                [GRID_LIST_ITEM_SLOTS.DESCRIPTION]: {
                  size: 12,
                  style: [styles.secondRow, textStyles.base],
                  color: (
                    (renderProps.isDisabled && 'disabled') ||
                    'quaternary'
                  ),
                  weight: 'regular',
                }
              },
            }],
            [NumberBadgeContext, {
              size: 16,
              color: 'critical',
              style: styles.firstRow,
            }],
            [IconContext, {
              slots: {
                [DEFAULT_SLOT]: {
                  size: 16,
                  color: (
                    (renderProps.isDisabled && 'disabled') ||
                    (isCritical && 'critical') ||
                    ((renderProps.isHovered || renderProps.isFocused || renderProps.isSelected) && 'primary') ||
                    'secondary'
                  ),
                  style: styles.firstRow,
                },
              }
            }],
            [CheckboxContext, {
              slots: {
                [DEFAULT_SLOT]: {
                  size: size >= 40 ? 20 : 16,
                  style: [styles.firstRow, styles.fullWidth],
                  isSelected: renderProps.isSelected,
                },
              }
            }],
            [TextButtonContext, {
              slots: {
                [DEFAULT_SLOT]: {
                  variant: 'primary',
                  size: 40,
                  style: styles.firstRow,
                }
              }
            }],
            [KbdContext, {
              style: [styles.firstRow, kbdStyles.base],
            }],
            [SwitchContext, {
              size: 20,
              style: styles.firstRow,
              isSelected: renderProps.isSelected,
            }],
            [IconButtonContext, {
              slots: {
                [DEFAULT_SLOT]: {
                  size: 32,
                  variant: 'ghost',
                },
                [GRID_LIST_ITEM_SLOTS.MORE_ICON_BUTTON]: {
                  size: 32,
                  variant: 'ghost',
                  style: [styles.firstRow, moreIconButtonStyles.base, renderProps.isHovered && moreIconButtonStyles.hovered],
                  children: <MoreVerticalStrokeIcon />
                }
              }
            }],
            [BadgeContext, {
              size: 20,
              isPastel: true,
              color: 'turquoise',
              style: [styles.firstRow, styles.fullWidth, badgeStyles.base],
            }],
            [AvatarContext, {
              size: 40,
              style: styles.firstRow,
            }],
          ]}>
            {isTextOnly ? <Text>{resolvedChildren}</Text> : resolvedChildren}
          </Provider>
        );
      }}
    </GridListItemCore>
  );
}) as <T>(p: GridListItemProps<T> & { ref?: ForwardedRef<HTMLDivElement> }) => ReactElement;

const styles = stylex.create({
  base: {
    borderRadius: 8,
    outline: 'none',
    paddingInline: 12,
    textDecoration: 'none',
    alignItems: 'center',
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-focus-visible])': $.opacity04,
    },
    boxSizing: 'border-box',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'grid',
    gridColumnGap: 8,
    gridRowGap: 4,
    gridTemplateColumns: 'auto 1fr auto',
    justifyItems: 'flex-start',
  },
  firstRow: { gridArea: '1' },
  secondRow: { gridArea: '2' },
  fullWidth: { gridColumn: 'span 3' },
});

const criticalStyles = stylex.create({
  base: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered])': $.surfaceCriticalSilent,
    },
  }
});

const textStyles = stylex.create({
  base: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'pre',
    width: '100%',
  }
});

const kbdStyles = stylex.create({
  base: {
    marginLeft: -7,
  }
});

const sizeVariants = stylex.create({
  56: {
    paddingBlock: 8,
    height: 56,
  },
  52: {
    height: 52,
  },
  50: {
    paddingBlock: 10,
    height: 50,
  },
  48: {
    paddingBlock: 12,
    height: 48,
  },
  40: {
    height: 40,
  },
});

const moreIconButtonStyles = stylex.create({
  base: {
    opacity: 0,
    marginRight: -6,
  },
  hovered: { opacity: 1 },
});

const badgeStyles = stylex.create({
  base: {
    maxWidth: '90%',
  },
});

const GRID_LIST_ITEM_SLOTS = {
  DESCRIPTION: "description",
  MORE_ICON_BUTTON: "more-icon-button",
};

export { GRID_LIST_ITEM_SLOTS, GridListItem, GridListItemContext, type GridListItemProps };
